import React, { FC, useEffect, useMemo, useState } from 'react';
import firebase from 'firebase/app';
import prettyBytes from 'pretty-bytes';
import { portalApi, PortalFile, PortalPreview, PortalProject, PortalProjectDetail } from 'util/adminApi';

/**
 * Task #1: what a client login sees (e.g. One3six). Only their own projects, read-only, with
 * downloads by links that expire after 15 minutes. All data comes from the clientPortal function;
 * a client login has no direct access to the database.
 *
 * Admins can open /portal?as=One3six|One3six%20Creative[&masters=1] to preview exactly this view.
 */

const LABELS: Record<string, string> = {
  preview: 'Preview',
  master: 'Master',
  CAD: 'Approval',
  distribution: 'Distribution',
};

const page: React.CSSProperties = { minHeight: '100vh', background: '#F5F6F8', color: '#16181D', fontFamily: 'inherit' };
const card: React.CSSProperties = { background: '#FFFFFF', border: '1px solid #E4E6EA', borderRadius: 14 };
const btn = (primary: boolean): React.CSSProperties => ({
  height: 40,
  padding: '0 14px',
  borderRadius: 10,
  border: primary ? 'none' : '1px solid #DADDE2',
  background: primary ? '#E5056A' : '#FFFFFF',
  color: primary ? '#FFFFFF' : '#16181D',
  fontSize: 14,
  fontWeight: primary ? 600 : 400,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

const fmtDate = (d: number | null) =>
  d ? new Date(d * (d < 1e11 ? 1000 : 1)).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

function readPreview(): PortalPreview {
  const q = new URLSearchParams(window.location.search);
  const as = q.get('as');
  if (!as) return undefined;
  return { previewClients: as.split('|').map((s) => s.trim()).filter(Boolean), previewMasters: q.get('masters') === '1' };
}

const Portal: FC = () => {
  const preview = useMemo(readPreview, []);
  const [clients, setClients] = useState<string[]>([]);
  const [projects, setProjects] = useState<PortalProject[] | null>(null);
  const [open, setOpen] = useState<PortalProjectDetail | null>(null);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [playing, setPlaying] = useState<{ name: string; url: string } | null>(null);

  useEffect(() => {
    portalApi
      .list(preview)
      .then((r) => {
        setClients(r.clients);
        setProjects(r.projects);
      })
      .catch((e) => setError((e as Error).message));
  }, [preview]);

  const openProject = (id: string) => {
    setBusy(id);
    setError('');
    portalApi
      .project(id, preview)
      .then((d) => {
        setOpen(d);
        window.scrollTo(0, 0);
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setBusy(null));
  };

  const getLink = (f: PortalFile, play: boolean) => {
    setBusy(f.id);
    setError('');
    portalApi
      .link(f.kind, f.id, play, preview)
      .then((r) => {
        if (play) setPlaying({ name: f.name, url: r.url });
        else window.location.href = r.url; // the link downloads; the page stays put
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setBusy(null));
  };

  const shown = (projects || []).filter((p) =>
    `${p.title} ${p.jobNumber} ${p.client} ${p.agency}`.toLowerCase().includes(filter.trim().toLowerCase()),
  );

  const fileRow = (f: PortalFile) => (
    <div
      key={f.kind + f.id}
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 14px', padding: '10px 16px', borderTop: '1px solid #EEF0F2' }}
    >
      <div style={{ flex: '1 1 320px', minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500, wordBreak: 'break-word' }}>{f.name}</div>
        <div style={{ fontSize: 12, color: '#5B616E' }}>
          {f.category ? `${LABELS[f.category] || f.category} · ` : ''}
          {f.size ? prettyBytes(f.size) : ''}
          {f.notes ? ` · ${f.notes}` : ''}
        </div>
      </div>
      {f.locked ? (
        <span style={{ fontSize: 13, color: '#5B616E' }}>Master: available on request</span>
      ) : (
        <>
          {f.category === 'preview' && (
            <button type="button" style={btn(false)} disabled={busy !== null} onClick={() => getLink(f, true)}>
              Play
            </button>
          )}
          <button type="button" style={btn(true)} disabled={busy !== null} onClick={() => getLink(f, false)}>
            {busy === f.id ? 'Preparing…' : 'Download'}
          </button>
        </>
      )}
    </div>
  );

  return (
    <div style={page}>
      <header style={{ background: '#FFFFFF', borderBottom: '1px solid #E4E6EA', padding: '14px 24px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>CoBox</div>
          <div style={{ fontSize: 13, color: '#5B616E' }}>Files for {clients.join(', ') || '…'}</div>
        </div>
        {preview && (
          <span style={{ fontSize: 13, fontWeight: 600, color: '#7A5300', background: '#FEF4D8', padding: '6px 12px', borderRadius: 999 }}>
            Admin preview: this is what that client login sees
          </span>
        )}
        <button type="button" style={btn(false)} onClick={() => firebase.auth().signOut()}>
          Sign out
        </button>
      </header>

      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px 48px' }}>
        {error && (
          <div role="alert" style={{ ...card, padding: 14, marginBottom: 16, borderColor: '#E8B4B0', color: '#B3261E' }}>
            {error}
          </div>
        )}

        {open ? (
          <>
            <button type="button" style={{ ...btn(false), marginBottom: 16 }} onClick={() => setOpen(null)}>
              ← All projects
            </button>
            <h1 style={{ margin: '0 0 4px', fontSize: 26 }}>{open.project.title}</h1>
            <p style={{ margin: '0 0 18px', color: '#5B616E', fontSize: 14 }}>
              {[open.project.client, open.project.agency, open.project.jobNumber, fmtDate(open.project.date)].filter(Boolean).join(' · ')}
            </p>
            {playing && (
              <div style={{ ...card, padding: 12, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <strong style={{ flex: 1, fontSize: 14, wordBreak: 'break-word' }}>{playing.name}</strong>
                  <button type="button" style={btn(false)} onClick={() => setPlaying(null)}>
                    Close
                  </button>
                </div>
                <video src={playing.url} controls autoPlay style={{ width: '100%', borderRadius: 10, background: '#000' }} />
              </div>
            )}
            {open.records.map((r) => {
              const files = open.files.filter((f) => f.recordID === r.id);
              return (
                <section key={r.id} style={{ ...card, marginBottom: 14, overflow: 'hidden' }}>
                  <h2 style={{ margin: 0, padding: '12px 16px', fontSize: 15 }}>
                    {r.keyNumber ? `${r.keyNumber} · ` : ''}
                    {r.title || 'Untitled record'}
                  </h2>
                  {files.length ? files.map(fileRow) : <p style={{ margin: 0, padding: '0 16px 12px', fontSize: 13, color: '#5B616E' }}>No files.</p>}
                </section>
              );
            })}
            {open.assets.length > 0 && (
              <section style={{ ...card, overflow: 'hidden' }}>
                <h2 style={{ margin: 0, padding: '12px 16px', fontSize: 15 }}>Project assets</h2>
                {open.assets.map(fileRow)}
              </section>
            )}
          </>
        ) : (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12, marginBottom: 16 }}>
              <h1 style={{ margin: 0, fontSize: 26, flex: 1 }}>Your projects</h1>
              <label style={{ fontSize: 13, color: '#5B616E', display: 'flex', flexDirection: 'column', gap: 4 }}>
                Find a project
                <input
                  type="search"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Title or job number"
                  style={{ height: 40, border: '1px solid #DADDE2', borderRadius: 10, padding: '0 12px', fontSize: 14, minWidth: 240 }}
                />
              </label>
            </div>
            {projects === null && !error && <p style={{ color: '#5B616E' }}>Loading your projects…</p>}
            {projects && projects.length === 0 && <p style={{ color: '#5B616E' }}>No projects yet.</p>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
              {shown.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => openProject(p.id)}
                  disabled={busy !== null}
                  style={{ ...card, textAlign: 'left', padding: 0, overflow: 'hidden', cursor: 'pointer', font: 'inherit', color: 'inherit' }}
                >
                  <div
                    style={{
                      aspectRatio: '16 / 9',
                      height: 135,
                      background: p.featureURL ? `#24506B url("${p.featureURL}") center / cover` : '#24506B',
                    }}
                  />
                  <div style={{ padding: '10px 12px 12px' }}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{busy === p.id ? 'Opening…' : p.title || 'Untitled'}</div>
                    <div style={{ fontSize: 12, color: '#5B616E', marginTop: 2 }}>
                      {[p.jobNumber, fmtDate(p.date)].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </main>
      <footer style={{ padding: '16px 24px', fontSize: 12, color: '#5B616E', textAlign: 'center' }}>
        Powered by <strong style={{ color: '#16181D' }}>Archy</strong> · Download links expire after 15 minutes · Every download is recorded
      </footer>
    </div>
  );
};

export default Portal;
